import React, { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useLogin } from "../../hooks/queries/useAuthMutations";

const LoginPage = ({
  onLogin,
  onGoToSignUp,
  onForgotPassword,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const login = useLogin();
  const isLoading = login.isPending;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      const session = await login.mutateAsync({ email, password });
      onLogin?.(session);
    } catch (err) {
      if (err?.code === "unauthorized" || err?.status === 401) {
        setError("Correo o contraseña incorrectos.");
      } else if (err?.code === "API_UNREACHABLE") {
        setError("Error de conexión. Inténtalo de nuevo.");
      } else if (err?.code === "rate_limited") {
        setError("Demasiados intentos. Espera un minuto e inténtalo de nuevo.");
      } else {
        setError(err?.message || "Error de conexión. Inténtalo de nuevo.");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 uppercase leading-tight tracking-wide mb-4 text-balance">
              Bienvenido a <span className="text-alert-300">Athletain</span>
            </h1>
            <p className="text-gray-600 text-sm leading-relaxed">
              Inicia sesión con tu cuenta. Te llevaremos automáticamente a tu
              espacio de atleta, reclutador o administrador.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="sr-only">
                Correo electrónico
              </label>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                required
              />
            </div>

            <div className="relative">
              <label htmlFor="login-password" className="sr-only">
                Contraseña
              </label>
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder="Contraseña"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                aria-pressed={showPassword}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-900 rounded"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-gray-500 hover:text-blue-900 transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <div className="flex justify-center pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "Iniciar sesión"
                )}
              </button>
            </div>
          </form>

          <div className="mt-7 text-center text-sm text-gray-500">
            ¿Eres nuevo en Athletain?{" "}
            <button
              type="button"
              onClick={onGoToSignUp}
              className="text-gray-700 font-medium hover:text-blue-900 underline underline-offset-2 transition-colors"
            >
              Crear una cuenta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
